import { useAuth0 } from "@auth0/auth0-react";
import {
  Box,
  TextField,
  Button,
  Typography,
  Container,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Switch,
} from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { useState, useEffect } from "react";

import API_Calls_Events from "@/api/calls/political/API_Calls_Events";
import Notifications from "@/components/Notifications/Notifications";
import SiteIcons from "@/components/SiteIcons/SiteIcons";
import { canManageEvents } from "@/utils/permissionsHandler";
import { formatTimestamp } from "@/utils/util";

function Events() {
  const { user } = useAuth0();
  const { createEvent, getEvents, postEventStatus } = API_Calls_Events();
  const [newEventForm, setNewEventForm] = useState({
    eventName: "",
    eventDate: null,
  });
  const [events, setEvents] = useState([]);
  const [notification, setNotification] = useState({});
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchEvents();
    // fetchEvents/getEvents are recreated each render; intentionally
    // fire only on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const clearForm = () => {
    setNewEventForm({ eventName: "", eventDate: null });
  };

  const handleCreateEvent = async () => {
    const eventData = {
      data_source: "member", // for now
      event_name: newEventForm.eventName,
      event_date: newEventForm.eventDate,
    };
    const response = await createEvent(eventData);
    if (!response.error && response.data) {
      setNotification({
        title: "Event created successfully",
        severity: "success",
        messageDuration: 3000,
      });
      fetchEvents();
      clearForm();
      return response;
    } else {
      console.log(response);
      setNotification({
        title: response.message || "There was a problem creating this event",
        message: response.details,
        severity: "error",
        messageDuration: 7000,
      });
    }
  };

  const fetchEvents = async () => {
    const res = await getEvents("global", false);
    setIsLoading(false);
    if (!res.error && res.data) {
      setEvents(res.data);
    } else {
      setNotification({
        title: res.message || "Unable to fetch existing Events",
        message: res.details,
        severity: "error",
        messageDuration: 7000,
      });
    }
  };

  const handleNewEventFormChange = (formKey) => (e) => {
    // Standardize value extraction
    const value = e && e.target ? e.target.value : e;

    setNewEventForm((prev) => ({
      ...prev,
      [formKey]: value,
    }));
  };

  const handleToggle = async (row, checked) => {
    const status = checked ? "active" : "inactive";
    const data = {
      global: status,
    };
    const res = await postEventStatus(row.event_id, data);

    if (!res.error) {
      setEvents((prevEvents) =>
        prevEvents.map((event) => {
          if (event.event_id === row.event_id) {
            return {
              ...event,
              event_status: status,
            };
          }
          return event;
        })
      );
    } else {
      setNotification({
        title: res.message || "Unable to change event status at this time",
        message: res.details,
        severity: "error",
        messageDuration: 7000,
      });
      console.error(res);
    }
  };

  return (
    <Container
      sx={{ display: "flex", justifyContent: "center", marginTop: "70px" }}
    >
      {canManageEvents(user) ? (
        <Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              maxWidth: "400px",
              marginBottom: "40px",
            }}
          >
            <Typography sx={{ marginBottom: "5px" }}>
              Create New Event
            </Typography>
            {/* EVENT NAME */}
            <TextField
              label="Event Name"
              variant="outlined"
              value={newEventForm.eventName}
              onChange={handleNewEventFormChange("eventName")}
              sx={{ backgroundColor: "white", marginBottom: "7px" }}
            />
            {/* EVENT DATE */}
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="Event Date (optional)"
                value={newEventForm.eventDate}
                onChange={handleNewEventFormChange("eventDate")}
                sx={{ backgroundColor: "white", marginBottom: "7px" }}
              />
            </LocalizationProvider>
            <Button
              className="siteBlue"
              disabled={newEventForm.eventName.length < 1}
              sx={{ width: "fit-content" }}
              onClick={handleCreateEvent}
            >
              Submit
            </Button>
          </Box>

          <TableContainer
            sx={{
              borderRadius: "6px",
              border: "solid 1px #80808040",
              backgroundColor: "white",
            }}
          >
            {isLoading ? (
              <Box
                sx={{
                  minWidth: 750,
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <SiteIcons
                  iconstring="circular-loader"
                  size="3rem"
                  sx={{ margin: "2rem" }}
                  page="sitewide-loader"
                />
              </Box>
            ) : (
              <Table
                sx={{ minWidth: 750 }}
                aria-label="simple table"
                data-testid="events-table"
              >
                <TableHead>
                  <TableRow>
                    <TableCell>Event name</TableCell>
                    <TableCell align="right">Event Date</TableCell>
                    <TableCell align="right">Local</TableCell>
                    <TableCell align="right">Active</TableCell>
                    {/* <TableCell align="right">Actions</TableCell> */}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {events.map((row, index) => (
                    <TableRow
                      key={index}
                      sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                    >
                      <TableCell
                        component="th"
                        scope="row"
                        sx={{
                          maxWidth: "300px",
                          wordBreak: "break-word",
                          lineHeight: "1.2",
                        }}
                      >
                        {row.event_name}
                      </TableCell>
                      <TableCell align="right">
                        {formatTimestamp(row.event_date, true)}
                      </TableCell>
                      <TableCell align="right">All locals</TableCell>
                      <TableCell align="right">
                        <Switch
                          checked={row.event_status === "active"}
                          onChange={(event, checked) =>
                            handleToggle(row, checked)
                          }
                          slotProps={{ input: { "aria-label": "controlled" } }}
                        />
                      </TableCell>
                      {/* <TableCell align="right">delete</TableCell> */}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </TableContainer>
        </Box>
      ) : (
        <Typography>
          Sorry, you don&apos;t have permission to manage events.
        </Typography>
      )}

      {!!Object.keys(notification).length && (
        <Notifications
          notification={notification}
          setNotification={setNotification}
        />
      )}
    </Container>
  );
}

export default Events;
